import "./MetaDocViewerWorkspace.scss";

import { KaboodleToaster } from "components/common";
import { FEATURE_FLAGS } from "constants/FeatureFlags";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SetStateAction } from "react";
import { STRING_TO_DATE } from "utils/date-utils";
import { useSelector } from "utils/redux";

import MetaDocPdfViewer from "../components/pdf-viewer/MetaDocPdfViewer";
import MetaDocTableOfContents from "../components/table-of-contents/MetaDocTableOfContents";
import MetaDocPageThumbnails from "../components/thumbnails/MetaDocPageThumbnails";
import MetaDocToolbar from "../components/toolbar/MetaDocToolbar";
import { useMetadocKeyboardShortcuts } from "../hooks/navigation/useMetadocKeyboardShortcuts";
import { useDocumentGrouping } from "../hooks/page-operations/useDocumentGrouping";
import { useLazyDocumentGroupUpdatesIntegration } from "../hooks/page-operations/useLazyDocumentGroupUpdatesIntegration";
import { useMetadocOperationController } from "../hooks/page-operations/useMetadocOperationController";
import { useMetadocSearch } from "../hooks/page-operations/useMetadocSearch";
import { useMetadocSummarySearch } from "../hooks/page-operations/useMetadocSummarySearch";
import { useDocumentGroupTagAssignment } from "../hooks/page-operations/useDocumentGroupTagAssignment";
import {
  METADOC_FIRST_PAGE,
  METADOC_FOCUS_RESTORE_DELAY_MS,
  METADOC_ZOOM_DEFAULT_PERCENT,
} from "../MetadocConstants";
import { getRunAiModelDecision } from "../rules/runAiModelRules";
import { getWorkspaceViewDecision } from "../rules/workspaceViewRules";
import MetaDocAIProcessingScreen from "../screens/MetaDocAIProcessingScreen";
import MetaDocOperationOverlayScreen from "../screens/MetaDocOperationOverlayScreen";
import { parseMetadataValue } from "../utils/codeMetadataUtils";
import { calculateNewCursorPosition } from "../utils/pageRangeUtils";
import {
  findAdjacentUntaggedPage,
  getUntaggedPages,
  type UntaggedPageDirection,
} from "../utils/untaggedPageUtils";
import {
  getNearestVisiblePage,
  getVisiblePageNumbersForTocFilters,
} from "../utils/tocFilterUtils";
import MetaDocAIInsightsPanel from "./MetaDocAIInsightsPanel";

import type { DocumentGroup } from "services/document-group-api";
import type { DocumentGroupTag } from "services/document-group-tag-api";

import {
  useGetMetadocMetadataQuery,
  type GroupingSource,
  type Metadoc,
  type MetadocMetadata,
} from "services/metadoc-api";
import type { PDFDocumentProxy } from "pdfjs-dist";
import type { BackgroundJobDataType } from "types/DataTypes";
import type {
  MetaDocPageThumbnailsHandle,
  MetaDocToolbarHandle,
  PageState,
  TabId,
} from "../metadoc.types";

// Stable empty-array defaults. A fresh `[]` literal in a default parameter
// is a new reference every render, which cascades through the useMemo
// chains below into effects that key off array identity -- that starved
// them of a stable "nothing changed" signal and caused a render loop.
const EMPTY_DOCUMENT_GROUPS: DocumentGroup[] = [];
const EMPTY_DOCUMENT_GROUP_TAGS: DocumentGroupTag[] = [];

const getStableDownloadIdentity = (url: string): string => {
  if (!url) {
    return "";
  }

  try {
    const parsedUrl = new URL(url);
    return `${parsedUrl.origin}${parsedUrl.pathname}`;
  } catch {
    return url.split("?")[0] ?? url;
  }
};

type TocMetadataSearchResult = {
  documentGroupId: number;
  pageNumber: number;
};

const MEDICATIONS_TAB_ID: TabId = "medications";
const CODES_TAB_ID: TabId = "codes";

const normalizeSearchValue = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number") {
    return `${value}`;
  }

  return "";
};

const getMedicationSearchText = (metadata: MetadocMetadata): string[] => {
  const parsedValue = parseMetadataValue(metadata.value);

  return [
    normalizeSearchValue(parsedValue?.name),
    normalizeSearchValue(parsedValue?.dosage),
    normalizeSearchValue(parsedValue?.frequency),
    normalizeSearchValue(metadata.date),
    normalizeSearchValue(metadata.provider_name),
  ];
};

const getCodeSearchText = (metadata: MetadocMetadata): string[] => {
  const parsedValue = parseMetadataValue(metadata.value);
  const dateDetails = Array.isArray(parsedValue?.date_details)
    ? parsedValue.date_details
    : [];

  return [
    normalizeSearchValue(parsedValue?.code),
    normalizeSearchValue(metadata.code_type),
    normalizeSearchValue(parsedValue?.code_type),
    normalizeSearchValue(parsedValue?.section),
    normalizeSearchValue(parsedValue?.facility),
    normalizeSearchValue(parsedValue?.condition_type),
    normalizeSearchValue(metadata.date),
    normalizeSearchValue(metadata.provider_name),
    ...dateDetails.flatMap((detail) => {
      if (!detail || typeof detail !== "object" || Array.isArray(detail)) {
        return [];
      }

      const codeDetail = detail as Record<string, unknown>;
      return [
        normalizeSearchValue(codeDetail.date),
        normalizeSearchValue(codeDetail.description),
        normalizeSearchValue(codeDetail.model_description),
        normalizeSearchValue(codeDetail.notes),
        normalizeSearchValue(codeDetail.page_number),
      ];
    }),
  ];
};

const getMatchedTocMetadataResults = ({
  documentGroupPageMap,
  metadataRows,
  query,
  valueSelector,
}: {
  documentGroupPageMap: Map<number, number>;
  metadataRows?: MetadocMetadata[];
  query: string;
  valueSelector: (metadata: MetadocMetadata) => string[];
}): TocMetadataSearchResult[] => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery || !metadataRows?.length) {
    return [];
  }

  const matchedResults = new Map<number, TocMetadataSearchResult>();

  for (const metadata of metadataRows) {
    if (typeof metadata.document_group !== "number") {
      continue;
    }

    const pageNumber = documentGroupPageMap.get(metadata.document_group);
    if (typeof pageNumber !== "number") {
      continue;
    }

    const matchesQuery = valueSelector(metadata).some((value) =>
      value.toLowerCase().includes(normalizedQuery),
    );
    if (!matchesQuery || matchedResults.has(metadata.document_group)) {
      continue;
    }

    matchedResults.set(metadata.document_group, {
      documentGroupId: metadata.document_group,
      pageNumber,
    });
  }

  return Array.from(matchedResults.values()).sort(
    (left, right) => left.pageNumber - right.pageNumber,
  );
};

type MetaDocViewerWorkspaceProps = {
  metadoc: Metadoc;
  tabId?: TabId;
  isProcessing?: boolean;
  processingJobStatus?: string;
  onProcessingComplete?: () => void;
  documentGroups?: DocumentGroup[];
  documentGroupTags?: DocumentGroupTag[];
  pageClassificationStats?: {
    coveredPages: number;
    totalPages: number;
    percentage: number;
  };
  onRunAIModel?: () => void;
  isRunningAIModel?: boolean;
  isRunAiModelDisabled?: boolean;
  runAiModelDisabledReason?: string;
  lastCompletedAIJob?: BackgroundJobDataType | null;
  isRunAiModelReady?: boolean;
  isMetadocFetching?: boolean;
  groupingSource?: GroupingSource;
  resolvedDownloadUrl: string;
  onGroupingSourceChange?: (source: GroupingSource) => void;
  canShowGroupingToggle?: boolean;
  onDeletionStateChange?: (isDeleting: boolean) => void;
  onTabChange?: (tabId: TabId) => void;
};

/*
 * state flow: hooks emit operation start/job/fail events, controller tracks async job phases,
 * and workspace applies ui lock, overlay payloads, and terminal cleanup/navigation side effects.
 */
const MetaDocViewerWorkspace = (props: MetaDocViewerWorkspaceProps) => {
  const {
    metadoc,
    tabId = "overview",
    isProcessing = false,
    processingJobStatus,
    onProcessingComplete,
    documentGroups = EMPTY_DOCUMENT_GROUPS,
    documentGroupTags = EMPTY_DOCUMENT_GROUP_TAGS,
    pageClassificationStats,
    onRunAIModel,
    isRunningAIModel = false,
    isRunAiModelDisabled = false,
    runAiModelDisabledReason,
    lastCompletedAIJob,
    isRunAiModelReady = false,
    isMetadocFetching = false,
    groupingSource = "OPS",
    resolvedDownloadUrl,
    onGroupingSourceChange,
    canShowGroupingToggle = false,
    onDeletionStateChange,
    onTabChange,
  } = props;
  const constants = useSelector((state) => state.shared.constants);
  const isSearchEnabled =
    constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.METADOC_SEARCH_ENABLED];
  const isLazyUpdatesEnabled =
    constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.METADOC_LAZY_UPDATES_ENABLED];
  const [currentPage, setCurrentPage] = useState<number>(METADOC_FIRST_PAGE);
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null);
  const [isPdfLoading, setIsPdfLoading] = useState<boolean>(true);
  const [zoomPercent, setZoomPercent] = useState<number>(
    METADOC_ZOOM_DEFAULT_PERCENT,
  );
  // track page states (e.g., "deleting") to apply visual styling via CSS classes
  const [pageStates, setPageStates] = useState<Map<number, PageState>>(
    new Map(),
  );
  const [isDeletingPages, setIsDeletingPages] = useState(false);
  const [deletingPageCount, setDeletingPageCount] = useState(0);
  const [deletingPageRanges, setDeletingPageRanges] = useState<number[][]>([]);
  const [isMovingPages, setIsMovingPages] = useState(false);
  const [movingPageCount, setMovingPageCount] = useState(0);
  const [movingPageRanges, setMovingPageRanges] = useState<number[][]>([]);
  const [movingTargetPosition, setMovingTargetPosition] = useState(0);
  const [isSelectingMoveTarget, setIsSelectingMoveTarget] = useState(false);
  const [onMoveTargetSelect, setOnMoveTargetSelect] = useState<
    ((targetPosition: number) => void) | null
  >(null);
  const toolbarRef = useRef<MetaDocToolbarHandle>(null);
  const thumbnailsRef = useRef<MetaDocPageThumbnailsHandle>(null);
  // stores the next page to jump to after grouping updates settle.
  const pendingNavigationRef = useRef<number | null>(null);
  // terminal-phase refs ensure toasts/cleanup run once per operation.
  const handledMoveTerminalPhaseRef = useRef<"success" | "failed" | null>(null);
  const handledDeleteTerminalPhaseRef = useRef<"success" | "failed" | null>(
    null,
  );
  // used to restore a stable cursor location after deletion reindexes pages.
  const [cursorPositionBeforeDeletion, setCursorPositionBeforeDeletion] =
    useState<number | null>(null);
  const [viewerDownloadUrl, setViewerDownloadUrl] =
    useState(resolvedDownloadUrl);
  const [viewerDownloadIdentity, setViewerDownloadIdentity] = useState(
    getStableDownloadIdentity(resolvedDownloadUrl),
  );
  const [selectedTocDocumentTypes, setSelectedTocDocumentTypes] = useState<
    string[] | null
  >(null);
  const [tocSearchQuery, setTocSearchQuery] = useState("");
  const [tocPageRange, setTocPageRange] = useState<{
    startPage: number | null;
    endPage: number | null;
  }>({
    startPage: null,
    endPage: null,
  });
  const [tocDateRange, setTocDateRange] = useState<{
    startDate: string | null;
    endDate: string | null;
  }>({
    startDate: null,
    endDate: null,
  });
  const [hideFilteredPages, setHideFilteredPages] = useState(false);
  const [summarySearchQuery, setSummarySearchQuery] = useState("");
  const [medicationSearchQuery, setMedicationSearchQuery] = useState("");
  const [codeSearchQuery, setCodeSearchQuery] = useState("");
  const [
    activeMedicationSearchResultIndex,
    setActiveMedicationSearchResultIndex,
  ] = useState(0);
  const [activeCodeSearchResultIndex, setActiveCodeSearchResultIndex] =
    useState(0);
  const lastAutoActivatedMedicationQueryRef = useRef("");
  const lastAutoActivatedCodeQueryRef = useRef("");
  const {
    state: operationState,
    startOperation,
    attachJob,
    failOperation,
    resetOperation,
    isUiLocked: isOperationUiLocked,
    overlayModel,
    showOverlay,
  } = useMetadocOperationController({
    metadocId: metadoc.id,
    isMetadocFetching,
    isPdfLoading,
  });

  const workspaceViewDecision = getWorkspaceViewDecision({
    cloneReason: metadoc.clone_reason ?? null,
    groupingSource,
    canShowGroupingToggle,
    hasPreAiRunDownloadUrl: Boolean(metadoc.pre_ai_run_download_url),
  });
  const {
    isAiAnalysisCloneView,
    effectiveGroupingSource,
    canShowGroupingToggle: canShowEffectiveGroupingToggle,
    isOpsPostAiReadOnly,
  } = workspaceViewDecision;

  const ensureDocumentTypeVisible = useCallback((documentType: string) => {
    setSelectedTocDocumentTypes((currentTypes) => {
      if (currentTypes === null || currentTypes.includes(documentType)) {
        return currentTypes;
      }
      return [...currentTypes, documentType];
    });
  }, []);

  // Tags aren't split by source the way documentGroups is (the model has no
  // source field), so scope them to the groups actually visible in this tab
  // by checking membership in the already-source-filtered documentGroups list.
  const documentGroupIds = useMemo(
    () => new Set(documentGroups.map((group) => group.id)),
    [documentGroups],
  );
  const scopedDocumentGroupTags = useMemo(
    () =>
      documentGroupTags.filter((tag) =>
        documentGroupIds.has(tag.document_group),
      ),
    [documentGroupTags, documentGroupIds],
  );

  // Lazy/bulk-edit pending state is strictly an Analyst (AI) tab feature —
  // Ops tab edits must always apply immediately, same as before lazy updates existed.
  const { tocEntries, facilityByDocumentGroupId, lazyTocProps } =
    useLazyDocumentGroupUpdatesIntegration({
      enabled:
        Boolean(isLazyUpdatesEnabled) && effectiveGroupingSource === "AI",
      documentGroups,
      documentGroupTags: scopedDocumentGroupTags,
      metadocId: metadoc.id,
      groupingSource: effectiveGroupingSource,
      onEnsureDocumentTypeVisible: ensureDocumentTypeVisible,
      startOperation,
      attachJob,
      resetOperation,
    });

  const totalPages = pdfDoc?.numPages ?? metadoc.page_count ?? 0;
  const { data: medicationMetadata = [] } = useGetMetadocMetadataQuery(
    { metadocId: metadoc.id, type: "medication" },
    { skip: groupingSource !== "AI" },
  );
  const { data: summaryMetadata = [] } = useGetMetadocMetadataQuery(
    { metadocId: metadoc.id, type: "summary" },
    { skip: groupingSource !== "AI" },
  );
  const { data: codeMetadata = [] } = useGetMetadocMetadataQuery(
    { metadocId: metadoc.id, type: "code" },
    { skip: groupingSource !== "AI" },
  );
  const tocDocumentTypes = useMemo(() => {
    return Array.from(new Set(tocEntries.map((entry) => entry.category)));
  }, [tocEntries]);
  const effectiveSelectedTocDocumentTypes =
    selectedTocDocumentTypes ?? tocDocumentTypes;
  const hasActiveDocumentTypeFilter =
    effectiveSelectedTocDocumentTypes.length !== tocDocumentTypes.length;
  const handleTocDocumentTypesChange = useCallback(
    (valueOrUpdater: SetStateAction<string[]>) => {
      setSelectedTocDocumentTypes((currentTypes) => {
        const nextBaseTypes = currentTypes ?? tocDocumentTypes;

        if (typeof valueOrUpdater === "function") {
          return valueOrUpdater(nextBaseTypes);
        }

        return valueOrUpdater;
      });
    },
    [tocDocumentTypes],
  );

  useEffect(() => {
    setSelectedTocDocumentTypes((currentTypes) => {
      if (currentTypes === null) {
        return tocDocumentTypes;
      }

      // Remove types that no longer exist
      const validTypes = currentTypes.filter((type) =>
        tocDocumentTypes.includes(type),
      );

      // Auto-select newly appearing types
      const newTypes = tocDocumentTypes.filter(
        (type) => !currentTypes.includes(type),
      );

      if (newTypes.length === 0 && validTypes.length === currentTypes.length) {
        return currentTypes;
      }

      return [...validTypes, ...newTypes];
    });
  }, [tocDocumentTypes]);

  useEffect(() => {
    setTocPageRange((currentRange) => {
      if (totalPages === 0) {
        if (currentRange.startPage === null && currentRange.endPage === null) {
          return currentRange;
        }

        return { startPage: null, endPage: null };
      }

      const nextStartPage =
        currentRange.startPage === null
          ? null
          : Math.min(currentRange.startPage, totalPages);
      const nextEndPage =
        currentRange.endPage === null
          ? null
          : Math.min(currentRange.endPage, totalPages);

      if (
        nextStartPage === currentRange.startPage &&
        nextEndPage === currentRange.endPage
      ) {
        return currentRange;
      }

      return {
        startPage: nextStartPage,
        endPage: nextEndPage,
      };
    });
  }, [totalPages]);
  const documentGroupPageMap = useMemo(
    () => new Map(documentGroups.map((group) => [group.id, group.start_pg])),
    [documentGroups],
  );
  const medicationSearchResults = useMemo(
    () =>
      getMatchedTocMetadataResults({
        documentGroupPageMap,
        metadataRows: medicationMetadata,
        query: medicationSearchQuery,
        valueSelector: getMedicationSearchText,
      }),
    [documentGroupPageMap, medicationMetadata, medicationSearchQuery],
  );
  const codeSearchResults = useMemo(
    () =>
      getMatchedTocMetadataResults({
        documentGroupPageMap,
        metadataRows: codeMetadata,
        query: codeSearchQuery,
        valueSelector: getCodeSearchText,
      }),
    [codeMetadata, codeSearchQuery, documentGroupPageMap],
  );
  const matchingMedicationDocumentGroupIds = useMemo(
    () =>
      new Set(medicationSearchResults.map((result) => result.documentGroupId)),
    [medicationSearchResults],
  );
  const matchingCodeDocumentGroupIds = useMemo(
    () => new Set(codeSearchResults.map((result) => result.documentGroupId)),
    [codeSearchResults],
  );
  const matchingDateDocumentGroupIds = useMemo(() => {
    const startDate = STRING_TO_DATE(tocDateRange.startDate);
    const endDate = STRING_TO_DATE(tocDateRange.endDate);

    if (!startDate && !endDate) {
      return new Set<number>();
    }

    const normalizedStartDate = startDate
      ? new Date(startDate.setHours(0, 0, 0, 0))
      : null;
    const normalizedEndDate = endDate
      ? new Date(endDate.setHours(23, 59, 59, 999))
      : null;
    const matchingDocumentGroupIds = new Set<number>();
    const allMetadataRows = [
      ...summaryMetadata,
      ...medicationMetadata,
      ...codeMetadata,
    ];

    const isDateInRange = (value: string | null | undefined) => {
      const parsedDate = STRING_TO_DATE(value ?? null);

      if (!parsedDate) {
        return false;
      }

      if (normalizedStartDate && parsedDate < normalizedStartDate) {
        return false;
      }

      if (normalizedEndDate && parsedDate > normalizedEndDate) {
        return false;
      }

      return true;
    };

    for (const metadataRow of allMetadataRows) {
      if (
        typeof metadataRow.document_group === "number" &&
        isDateInRange(metadataRow.date)
      ) {
        matchingDocumentGroupIds.add(metadataRow.document_group);
      }
    }

    for (const entry of tocEntries) {
      if (
        typeof entry.documentGroupId === "number" &&
        isDateInRange(entry.dateOfService)
      ) {
        matchingDocumentGroupIds.add(entry.documentGroupId);
      }
    }

    return matchingDocumentGroupIds;
  }, [
    codeMetadata,
    medicationMetadata,
    summaryMetadata,
    tocDateRange.endDate,
    tocDateRange.startDate,
    tocEntries,
  ]);
  const hasActiveEntryVisibilityFilter =
    hasActiveDocumentTypeFilter ||
    tocDateRange.startDate !== null ||
    tocDateRange.endDate !== null ||
    tocSearchQuery.trim().length > 0 ||
    medicationSearchQuery.trim().length > 0 ||
    codeSearchQuery.trim().length > 0;
  const filteredVisiblePageNumbers = useMemo(
    () =>
      getVisiblePageNumbersForTocFilters({
        entries: tocEntries,
        totalPages,
        facilityByDocumentGroupId,
        hasEntryVisibilityFilter: hasActiveEntryVisibilityFilter,
        selectedDocumentTypes: effectiveSelectedTocDocumentTypes,
        hasExplicitDocumentTypeSelection: selectedTocDocumentTypes !== null,
        searchQuery: tocSearchQuery,
        dateRangeStart: tocDateRange.startDate,
        dateRangeEnd: tocDateRange.endDate,
        matchingDateDocumentGroupIds,
        pageRangeStart: tocPageRange.startPage,
        pageRangeEnd: tocPageRange.endPage,
        matchingMedicationDocumentGroupIds,
        matchingCodeDocumentGroupIds,
        medicationSearchQuery,
        codeSearchQuery,
      }),
    [
      codeSearchQuery,
      effectiveSelectedTocDocumentTypes,
      facilityByDocumentGroupId,
      hasActiveEntryVisibilityFilter,
      matchingDateDocumentGroupIds,
      selectedTocDocumentTypes,
      matchingCodeDocumentGroupIds,
      matchingMedicationDocumentGroupIds,
      medicationSearchQuery,
      tocDateRange.endDate,
      tocDateRange.startDate,
      tocEntries,
      tocPageRange.endPage,
      tocPageRange.startPage,
      tocSearchQuery,
      totalPages,
    ],
  );
  const visiblePageNumbers = useMemo(() => {
    if (!hideFilteredPages) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    return filteredVisiblePageNumbers;
  }, [filteredVisiblePageNumbers, hideFilteredPages, totalPages]);
  const visiblePageNumberSet = useMemo(
    () => new Set(visiblePageNumbers),
    [visiblePageNumbers],
  );
  const visiblePageRangeStart = visiblePageNumbers[0] ?? METADOC_FIRST_PAGE;
  const visiblePageRangeEnd =
    visiblePageNumbers[visiblePageNumbers.length - 1] ??
    Math.max(totalPages, METADOC_FIRST_PAGE);
  const isSelectiveOpsReadonlyMode =
    isOpsPostAiReadOnly &&
    effectiveGroupingSource === "OPS" &&
    metadoc.clone_reason === "ai_analysis";
  const isAIInsightsTab =
    tabId === "executive-summary" ||
    tabId === "per-record" ||
    tabId === "codes" ||
    tabId === "medications" ||
    tabId === "not-medicare-covered";
  const isAITabBlockedByOPS =
    isAIInsightsTab && effectiveGroupingSource === "OPS";
  const runAiModelDecision = getRunAiModelDecision({
    mode: effectiveGroupingSource === "AI" ? "analyst" : "ops",
    hasPreAiRun: Boolean(metadoc.pre_ai_run_download_url),
    cloneReason: metadoc.clone_reason ?? null,
  });
  const effectiveRunAiModelDecision = isAiAnalysisCloneView
    ? {
        ...runAiModelDecision,
        showRunAiModelButton: true,
      }
    : runAiModelDecision;
  const hasDocumentsForAiAnalysisClone =
    (metadoc.source_documents?.length ?? 0) > 0 &&
    (metadoc.page_count ?? 0) > 0;
  const shouldDisableAiAnalysisCloneRunButton =
    isAiAnalysisCloneView && !hasDocumentsForAiAnalysisClone;
  const effectiveIsRunAiModelDisabled =
    isRunAiModelDisabled || shouldDisableAiAnalysisCloneRunButton;
  const effectiveRunAiModelDisabledReason =
    shouldDisableAiAnalysisCloneRunButton
      ? "Add documents to this MetaDoc before running AI model."
      : runAiModelDisabledReason;
  const nextResolvedDownloadIdentity =
    getStableDownloadIdentity(resolvedDownloadUrl);
  const viewerResolvedDownloadIdentity =
    getStableDownloadIdentity(viewerDownloadUrl);

  useEffect(() => {
    if (tocDocumentTypes.length === 0) {
      // Bail out via the updater form so an already-empty selection keeps
      // its existing reference. Passing a bare [] here would set a new
      // reference every run, and since tocDocumentTypes can change
      // reference without changing contents, that would re-trigger this
      // effect forever.
      setSelectedTocDocumentTypes((currentSelection) =>
        currentSelection.length === 0 ? currentSelection : [],
      );
      return;
    }

    setSelectedTocDocumentTypes((currentSelection) => {
      if (currentSelection.length === 0) {
        return currentSelection;
      }

      const availableTypeSet = new Set(tocDocumentTypes);
      const nextSelection = currentSelection.filter((type) =>
        availableTypeSet.has(type),
      );

      const hasSameSelection =
        nextSelection.length === currentSelection.length &&
        nextSelection.every((type, index) => type === currentSelection[index]);

      return hasSameSelection ? currentSelection : nextSelection;
    });
  }, [tocDocumentTypes]);

  useEffect(() => {
    if (!resolvedDownloadUrl) {
      return;
    }

    if (nextResolvedDownloadIdentity === viewerResolvedDownloadIdentity) {
      return;
    }

    setViewerDownloadUrl(resolvedDownloadUrl);
    setViewerDownloadIdentity(nextResolvedDownloadIdentity);
  }, [
    resolvedDownloadUrl,
    nextResolvedDownloadIdentity,
    viewerResolvedDownloadIdentity,
  ]);

  const formatPageRanges = (pageRanges: number[][]) => {
    return pageRanges
      .map(([start, end]) => (start === end ? `${start}` : `${start}-${end}`))
      .join(", ");
  };

  const handlePageChange = useCallback(
    (newPage: number) => {
      const clampedPage = Math.min(
        Math.max(newPage, METADOC_FIRST_PAGE),
        Math.max(totalPages, METADOC_FIRST_PAGE),
      );
      const nextVisiblePage = getNearestVisiblePage(
        clampedPage,
        visiblePageNumbers,
      );

      if (
        nextVisiblePage !== null &&
        nextVisiblePage >= METADOC_FIRST_PAGE &&
        nextVisiblePage <= Math.max(totalPages, METADOC_FIRST_PAGE)
      ) {
        setCurrentPage(nextVisiblePage);
      }
    },
    [totalPages, visiblePageNumbers],
  );

  const { assignDocumentType, assignDocumentTypeToRange, deleteEntry } =
    useDocumentGrouping(
      metadoc.id,
      documentGroups,
      selectedPages,
      setSelectedPages,
      effectiveGroupingSource,
      () => {
        // return focus to thumbnails after successful assignment
        setTimeout(() => {
          thumbnailsRef.current?.focusThumbnails();
        }, METADOC_FOCUS_RESTORE_DELAY_MS);

        // store the last assigned page to navigate after documentGroups updates
        const sortedPages = Array.from(selectedPages).sort((a, b) => a - b);
        const lastAssignedPage =
          sortedPages[sortedPages.length - 1] || currentPage;
        pendingNavigationRef.current = lastAssignedPage + 1;
      },
      attachJob,
      (isRequesting) => {
        if (isRequesting) {
          startOperation("regenerate");
          return;
        }

        resetOperation();
      },
    );

  const { assignDocumentGroupTag, removeDocumentGroupTag } =
    useDocumentGroupTagAssignment();

  const untaggedPages = useMemo(
    () => getUntaggedPages(documentGroups, totalPages),
    [documentGroups, totalPages],
  );

  // handle navigation to next uncategorized page after document groups update
  useEffect(() => {
    // runs after documentGroups refresh; consumes pendingNavigationRef once.
    if (pendingNavigationRef.current === null) {
      return;
    }

    const startPage = pendingNavigationRef.current;
    const nextUncategorizedPage =
      untaggedPages.find((page) => page >= startPage) ?? null;

    if (nextUncategorizedPage) {
      handlePageChange(nextUncategorizedPage);
      // also select the page so user can immediately start categorizing it
      setSelectedPages(new Set([nextUncategorizedPage]));
    }

    // clear the pending navigation
    pendingNavigationRef.current = null;
  }, [untaggedPages, handlePageChange]);

  const handleGoToUntaggedPage = useCallback(
    (direction: UntaggedPageDirection) => {
      const targetPage = findAdjacentUntaggedPage(
        currentPage,
        direction,
        untaggedPages,
      );
      if (targetPage === null) {
        return;
      }

      // Filters only hide pages in Analyst view, so every Ops page is visible.
      handlePageChange(targetPage);
      setSelectedPages(new Set([targetPage]));
    },
    [currentPage, untaggedPages, handlePageChange],
  );

  const goToUntaggedPage =
    effectiveGroupingSource === "AI" || metadoc.status !== "completed"
      ? undefined
      : handleGoToUntaggedPage;

  useEffect(() => {
    // guards against stale page index when a new PDF with fewer pages loads.
    if (!pdfDoc) return;
    if (currentPage > pdfDoc.numPages) {
      setCurrentPage(pdfDoc.numPages);
    }
  }, [currentPage, pdfDoc]);

  useEffect(() => {
    if (totalPages === 0 || visiblePageNumbers.length === 0) {
      return;
    }

    if (!visiblePageNumberSet.has(currentPage)) {
      const nextVisiblePage = getNearestVisiblePage(
        currentPage,
        visiblePageNumbers,
      );

      if (nextVisiblePage !== null) {
        setCurrentPage(nextVisiblePage);
      }
    }
  }, [currentPage, totalPages, visiblePageNumbers, visiblePageNumberSet]);

  useEffect(() => {
    // clears selection in ops post-ai readonly mode to avoid invalid actions.
    if (
      !isOpsPostAiReadOnly ||
      isSelectiveOpsReadonlyMode ||
      selectedPages.size === 0
    ) {
      return;
    }

    setSelectedPages(new Set());
  }, [isOpsPostAiReadOnly, isSelectiveOpsReadonlyMode, selectedPages.size]);

  useEffect(() => {
    setSelectedPages((currentSelection) => {
      const nextSelection = new Set(
        Array.from(currentSelection).filter((page) =>
          visiblePageNumberSet.has(page),
        ),
      );

      return nextSelection.size === currentSelection.size
        ? currentSelection
        : nextSelection;
    });
  }, [visiblePageNumberSet]);

  useEffect(() => {
    setPdfDoc(null);
    setIsPdfLoading(true);
    setCurrentPage(METADOC_FIRST_PAGE);
    setSelectedPages(new Set());
    setPageStates(new Map());
    setZoomPercent(METADOC_ZOOM_DEFAULT_PERCENT);
    setTocDateRange({
      startDate: null,
      endDate: null,
    });
    setHideFilteredPages(false);
  }, [viewerDownloadIdentity, viewerDownloadUrl, metadoc.id]);

  useEffect(() => {
    // movement terminal-phase handler.
    // controller owns async lifecycle; workspace owns UI cleanup and navigation.
    if (operationState.operationType !== "move") {
      handledMoveTerminalPhaseRef.current = null;
      return;
    }

    if (
      operationState.phase === "success" &&
      handledMoveTerminalPhaseRef.current !== "success"
    ) {
      // move success path: notify user, reposition cursor, clear move UI state.
      handledMoveTerminalPhaseRef.current = "success";
      KaboodleToaster.success("Pages moved successfully");
      if (
        movingTargetPosition >= METADOC_FIRST_PAGE &&
        movingTargetPosition <= totalPages
      ) {
        handlePageChange(movingTargetPosition);
      } else if (totalPages > 0) {
        handlePageChange(totalPages);
      }
      setIsMovingPages(false);
      setMovingPageCount(0);
      setMovingPageRanges([]);
      setMovingTargetPosition(0);
      setPageStates(new Map());
      resetOperation();
      return;
    }

    if (
      operationState.phase === "failed" &&
      handledMoveTerminalPhaseRef.current !== "failed"
    ) {
      // move failure path: notify and fully clear move-specific UI state.
      handledMoveTerminalPhaseRef.current = "failed";
      KaboodleToaster.error(
        `Page movement failed: ${operationState.statusMessage || "Unknown error"}`,
      );
      setIsMovingPages(false);
      setMovingPageCount(0);
      setMovingPageRanges([]);
      setMovingTargetPosition(0);
      setPageStates(new Map());
      resetOperation();
    }
  }, [
    operationState.operationType,
    operationState.phase,
    operationState.statusMessage,
    handlePageChange,
    movingTargetPosition,
    resetOperation,
    totalPages,
  ]);

  useEffect(() => {
    // deletion terminal-phase handler.
    // on success we also restore cursor to a stable page after removed ranges.
    if (operationState.operationType !== "delete") {
      handledDeleteTerminalPhaseRef.current = null;
      return;
    }

    if (
      operationState.phase === "success" &&
      handledDeleteTerminalPhaseRef.current !== "success"
    ) {
      // delete success path: notify, recompute cursor, then clear delete UI state.
      handledDeleteTerminalPhaseRef.current = "success";
      KaboodleToaster.success("Pages deleted successfully");

      if (cursorPositionBeforeDeletion !== null) {
        const newPosition = calculateNewCursorPosition(
          cursorPositionBeforeDeletion,
          deletingPageRanges,
          totalPages,
        );

        if (newPosition >= METADOC_FIRST_PAGE && newPosition <= totalPages) {
          handlePageChange(newPosition);
        } else if (totalPages > 0) {
          handlePageChange(totalPages);
        }
      }

      setIsDeletingPages(false);
      setDeletingPageCount(0);
      setDeletingPageRanges([]);
      setCursorPositionBeforeDeletion(null);
      setPageStates(new Map());
      onDeletionStateChange?.(false);
      resetOperation();
      return;
    }

    if (
      operationState.phase === "failed" &&
      handledDeleteTerminalPhaseRef.current !== "failed"
    ) {
      // delete failure path: notify and fully clear delete-specific UI state.
      handledDeleteTerminalPhaseRef.current = "failed";
      KaboodleToaster.error(
        `Page deletion failed: ${operationState.statusMessage || "Unknown error"}`,
      );
      setIsDeletingPages(false);
      setDeletingPageCount(0);
      setDeletingPageRanges([]);
      setCursorPositionBeforeDeletion(null);
      setPageStates(new Map());
      onDeletionStateChange?.(false);
      resetOperation();
    }
  }, [
    cursorPositionBeforeDeletion,
    deletingPageRanges,
    operationState.operationType,
    operationState.phase,
    operationState.statusMessage,
    handlePageChange,
    totalPages,
    onDeletionStateChange,
    resetOperation,
  ]);

  const handleCurrentPageUpdate = (page: number) => {
    handlePageChange(page);
  };

  const handlePdfLoadingChange = useCallback((loading: boolean) => {
    setIsPdfLoading(loading);
  }, []);

  const handleSelectionChange = (pages: Set<number>) => {
    setSelectedPages(pages);
  };

  const handleClearSelection = () => {
    setSelectedPages(new Set());
  };

  const handleDeletionStateChange = (
    isDeleting: boolean,
    pageCount: number,
    pageRanges: number[][],
  ) => {
    // local delete overlay payload for rendering while operation is active.
    setIsDeletingPages(isDeleting);
    setDeletingPageCount(pageCount);
    setDeletingPageRanges(pageRanges);
    onDeletionStateChange?.(isDeleting);
  };

  const handleMovementStateChange = (
    isMoving: boolean,
    pageCount: number,
    pageRanges: number[][],
    targetPosition: number,
  ) => {
    // local move overlay payload for rendering while operation is active.
    setIsMovingPages(isMoving);
    setMovingPageCount(pageCount);
    setMovingPageRanges(pageRanges);
    setMovingTargetPosition(targetPosition);
  };

  const [onCancelMoveTargetSelection, setOnCancelMoveTargetSelection] =
    useState<(() => void) | null>(null);

  const handleMoveTargetSelectionChange = (
    isSelecting: boolean,
    onTargetSelect: ((targetPosition: number) => void) | null,
    onCancel?: (() => void) | null,
  ) => {
    setIsSelectingMoveTarget(isSelecting);
    setOnMoveTargetSelect(() => onTargetSelect);
    setOnCancelMoveTargetSelection(() => onCancel || null);
  };

  const {
    activeSearchResultIndex,
    canGoToNextSearchResult,
    canGoToPreviousSearchResult,
    handleNextSearchResult,
    handlePreviousSearchResult,
    handleSearch,
    handleStartIndexing,
    highlightsByPage,
    isSearchIndexFetching,
    isSearchReady,
    isSearching,
    isStartingIndexing,
    lastSearchQuery,
    searchIndexStatus,
    searchNavigableResultCount,
    searchResultCount,
  } = useMetadocSearch({
    metadocId: metadoc.id,
    onPageChange: handlePageChange,
    groupingSource: effectiveGroupingSource,
    hasPreAiRunDownloadUrl: Boolean(metadoc.pre_ai_run_download_url),
  });
  const {
    activeResult: activeSummarySearchResult,
    clearSearch: clearSummarySearch,
    handleSearch: handleSummarySearch,
    handleNextResult: handleNextSummarySearchResult,
    handlePreviousResult: handlePreviousSummarySearchResult,
    isSearching: isSummarySearching,
    lastSearchQuery: lastSummarySearchQuery,
    navigableResultCount: summarySearchNavigableResultCount,
    resultCount: summarySearchResultCount,
  } = useMetadocSummarySearch({
    metadocId: metadoc.id,
    onResultActivate: (result) => {
      onTabChange?.(result.tab_id);

      if (typeof result.page_number === "number") {
        handlePageChange(result.page_number);
      }
    },
  });
  const resetExclusiveTocFilters = useCallback(
    (
      activeFilter:
        | "tocSearch"
        | "pageRange"
        | "dateRange"
        | "summarySearch"
        | "medicationSearch"
        | "codeSearch",
    ) => {
      if (activeFilter !== "tocSearch") {
        setTocSearchQuery("");
      }

      if (activeFilter !== "pageRange") {
        setTocPageRange({
          startPage: null,
          endPage: null,
        });
      }

      if (activeFilter !== "dateRange") {
        setTocDateRange({
          startDate: null,
          endDate: null,
        });
      }

      if (activeFilter !== "summarySearch") {
        setSummarySearchQuery("");
        clearSummarySearch();
      }

      if (activeFilter !== "medicationSearch") {
        setMedicationSearchQuery("");
        setActiveMedicationSearchResultIndex(0);
      }

      if (activeFilter !== "codeSearch") {
        setCodeSearchQuery("");
        setActiveCodeSearchResultIndex(0);
      }
    },
    [clearSummarySearch],
  );
  const handleTocPageRangeChange = useCallback(
    ({
      startPage,
      endPage,
    }: {
      startPage: number | null;
      endPage: number | null;
    }) => {
      const nextPageRange = {
        startPage:
          startPage === null
            ? null
            : Math.min(Math.max(startPage, 1), totalPages),
        endPage:
          endPage === null ? null : Math.min(Math.max(endPage, 1), totalPages),
      };

      setTocPageRange(nextPageRange);

      if (nextPageRange.startPage === null && nextPageRange.endPage === null) {
        return;
      }

      resetExclusiveTocFilters("pageRange");
    },
    [resetExclusiveTocFilters, totalPages],
  );
  const handleTocDateRangeChange = useCallback(
    ({
      startDate,
      endDate,
    }: {
      startDate: string | null;
      endDate: string | null;
    }) => {
      const nextDateRange = {
        startDate,
        endDate,
      };

      setTocDateRange(nextDateRange);

      if (nextDateRange.startDate === null && nextDateRange.endDate === null) {
        return;
      }

      resetExclusiveTocFilters("dateRange");
    },
    [resetExclusiveTocFilters],
  );

  const handleTocFilterSearchChange = useCallback(
    (value: string) => {
      setTocSearchQuery(value);

      if (value.trim().length === 0) {
        return;
      }

      resetExclusiveTocFilters("tocSearch");
    },
    [resetExclusiveTocFilters],
  );

  const handleSummaryFilterSearchChange = useCallback(
    (value: string) => {
      setSummarySearchQuery(value);

      if (value.trim().length === 0) {
        if (!lastSummarySearchQuery) {
          return;
        }

        clearSummarySearch();
        return;
      }

      resetExclusiveTocFilters("summarySearch");
      clearSummarySearch();
    },
    [clearSummarySearch, lastSummarySearchQuery, resetExclusiveTocFilters],
  );

  const handleMedicationFilterSearchChange = useCallback(
    (value: string) => {
      setMedicationSearchQuery(value);

      if (value.trim().length === 0) {
        return;
      }

      resetExclusiveTocFilters("medicationSearch");
    },
    [resetExclusiveTocFilters],
  );

  const handleCodeFilterSearchChange = useCallback(
    (value: string) => {
      setCodeSearchQuery(value);

      if (value.trim().length === 0) {
        return;
      }

      resetExclusiveTocFilters("codeSearch");
    },
    [resetExclusiveTocFilters],
  );

  useEffect(() => {
    setActiveMedicationSearchResultIndex(0);
  }, [medicationSearchQuery, metadoc.id]);

  useEffect(() => {
    setActiveCodeSearchResultIndex(0);
  }, [codeSearchQuery, metadoc.id]);

  useEffect(() => {
    if (medicationSearchResults.length === 0) {
      setActiveMedicationSearchResultIndex(0);
      return;
    }

    setActiveMedicationSearchResultIndex((currentIndex) =>
      Math.min(currentIndex, medicationSearchResults.length - 1),
    );
  }, [medicationSearchResults]);

  useEffect(() => {
    if (codeSearchResults.length === 0) {
      setActiveCodeSearchResultIndex(0);
      return;
    }

    setActiveCodeSearchResultIndex((currentIndex) =>
      Math.min(currentIndex, codeSearchResults.length - 1),
    );
  }, [codeSearchResults]);

  const handleActivateMedicationSearchResult = useCallback(
    (resultIndex: number) => {
      const nextResult = medicationSearchResults[resultIndex];
      if (!nextResult) {
        return;
      }

      setActiveMedicationSearchResultIndex(resultIndex);
      onTabChange?.(MEDICATIONS_TAB_ID);
      handlePageChange(nextResult.pageNumber);
    },
    [handlePageChange, medicationSearchResults, onTabChange],
  );

  const handleActivateCodeSearchResult = useCallback(
    (resultIndex: number) => {
      const nextResult = codeSearchResults[resultIndex];
      if (!nextResult) {
        return;
      }

      setActiveCodeSearchResultIndex(resultIndex);
      onTabChange?.(CODES_TAB_ID);
      handlePageChange(nextResult.pageNumber);
    },
    [codeSearchResults, handlePageChange, onTabChange],
  );

  const handleNextMedicationSearchResult = useCallback(() => {
    if (medicationSearchResults.length <= 1) {
      return;
    }

    const nextIndex =
      (activeMedicationSearchResultIndex + 1) % medicationSearchResults.length;
    handleActivateMedicationSearchResult(nextIndex);
  }, [
    activeMedicationSearchResultIndex,
    handleActivateMedicationSearchResult,
    medicationSearchResults.length,
  ]);

  const handlePreviousMedicationSearchResult = useCallback(() => {
    if (medicationSearchResults.length <= 1) {
      return;
    }

    const previousIndex =
      (activeMedicationSearchResultIndex - 1 + medicationSearchResults.length) %
      medicationSearchResults.length;
    handleActivateMedicationSearchResult(previousIndex);
  }, [
    activeMedicationSearchResultIndex,
    handleActivateMedicationSearchResult,
    medicationSearchResults.length,
  ]);

  const handleNextCodeSearchResult = useCallback(() => {
    if (codeSearchResults.length <= 1) {
      return;
    }

    const nextIndex =
      (activeCodeSearchResultIndex + 1) % codeSearchResults.length;
    handleActivateCodeSearchResult(nextIndex);
  }, [
    activeCodeSearchResultIndex,
    codeSearchResults.length,
    handleActivateCodeSearchResult,
  ]);

  const handlePreviousCodeSearchResult = useCallback(() => {
    if (codeSearchResults.length <= 1) {
      return;
    }

    const previousIndex =
      (activeCodeSearchResultIndex - 1 + codeSearchResults.length) %
      codeSearchResults.length;
    handleActivateCodeSearchResult(previousIndex);
  }, [
    activeCodeSearchResultIndex,
    codeSearchResults.length,
    handleActivateCodeSearchResult,
  ]);

  useEffect(() => {
    const normalizedQuery = medicationSearchQuery.trim();
    if (!normalizedQuery) {
      lastAutoActivatedMedicationQueryRef.current = "";
      return;
    }

    if (medicationSearchResults.length === 0) {
      return;
    }

    if (lastAutoActivatedMedicationQueryRef.current === normalizedQuery) {
      return;
    }

    lastAutoActivatedMedicationQueryRef.current = normalizedQuery;
    handleActivateMedicationSearchResult(0);
  }, [
    handleActivateMedicationSearchResult,
    medicationSearchQuery,
    medicationSearchResults,
  ]);

  useEffect(() => {
    const normalizedQuery = codeSearchQuery.trim();
    if (!normalizedQuery) {
      lastAutoActivatedCodeQueryRef.current = "";
      return;
    }

    if (codeSearchResults.length === 0) {
      return;
    }

    if (lastAutoActivatedCodeQueryRef.current === normalizedQuery) {
      return;
    }

    lastAutoActivatedCodeQueryRef.current = normalizedQuery;
    handleActivateCodeSearchResult(0);
  }, [codeSearchQuery, codeSearchResults, handleActivateCodeSearchResult]);

  // setup global keyboard shortcuts
  const isTocDisabled =
    isPdfLoading ||
    isProcessing ||
    isDeletingPages ||
    isMovingPages ||
    isOperationUiLocked ||
    isSelectingMoveTarget;

  useMetadocKeyboardShortcuts(
    currentPage,
    totalPages,
    handlePageChange,
    selectedPages.size,
    handleClearSelection,
    toolbarRef,
    isTocDisabled ? undefined : goToUntaggedPage,
  );

  // AI insights/medications/codes are keyed off this group's id.
  const activeDocumentGroup = documentGroups.find(
    (group) => currentPage >= group.start_pg && currentPage <= group.end_pg,
  );

  if (isAITabBlockedByOPS) {
    return (
      <div className="metadoc-empty-state">
        <span className="material-icons metadoc-empty-icon">info</span>
        <p className="metadoc-empty-message">
          AI analysis tabs are available only in AI grouping mode.
        </p>
      </div>
    );
  }

  return (
    <div
      className={`metadoc-viewer-workspace ${isProcessing || isDeletingPages || isMovingPages || isOperationUiLocked ? "metadoc-viewer-workspace--processing" : ""}`}
    >
      <MetaDocAIInsightsPanel
        metadocId={metadoc.id}
        tabId={tabId}
        activeDocumentGroup={activeDocumentGroup}
        documentGroups={documentGroups}
        activeMedicationFilterQuery={medicationSearchQuery}
        activeCodeFilterQuery={codeSearchQuery}
        activeSummarySearch={{
          query: lastSummarySearchQuery,
          result: activeSummarySearchResult,
        }}
      />
      <MetaDocToolbar
        ref={toolbarRef}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        zoomPercent={zoomPercent}
        onZoomPercentChange={setZoomPercent}
        selectedPages={selectedPages}
        onClearSelection={handleClearSelection}
        versionHash={metadoc.version_hash}
        groupingSource={effectiveGroupingSource}
        disabled={
          isPdfLoading ||
          isProcessing ||
          isDeletingPages ||
          isMovingPages ||
          isOperationUiLocked
        }
        documentGroups={documentGroups}
        onSetPageStates={setPageStates}
        metadocId={metadoc.id}
        pageClassificationStats={pageClassificationStats}
        metadocStatus={metadoc.status}
        onRunAIModel={onRunAIModel}
        isRunningAIModel={isRunningAIModel}
        isRunAiModelDisabled={effectiveIsRunAiModelDisabled}
        runAiModelDisabledReason={effectiveRunAiModelDisabledReason}
        lastCompletedAIJob={lastCompletedAIJob}
        isRunAiModelReady={isRunAiModelReady}
        isOpsPostAiReadOnly={isOpsPostAiReadOnly}
        runAiModelDecision={effectiveRunAiModelDecision}
        cloneReason={metadoc.clone_reason ?? null}
        onDeletionStateChange={handleDeletionStateChange}
        onDeletionOperationStart={(meta) => {
          setCursorPositionBeforeDeletion(meta.cursorPositionBeforeDeletion);
          startOperation("delete", {
            pageCount: meta.pageCount,
            pageRanges: meta.pageRanges,
          });
        }}
        onDeletionOperationJobCreated={attachJob}
        onDeletionOperationFail={(statusMessage) => {
          failOperation(statusMessage);
        }}
        onDeletionOperationReset={resetOperation}
        onMovementStateChange={handleMovementStateChange}
        onMovementOperationStart={(meta) => {
          startOperation("move", {
            pageCount: meta.pageCount,
            pageRanges: meta.pageRanges,
            targetPosition: meta.targetPosition,
          });
        }}
        onMovementOperationFail={(statusMessage) => {
          failOperation(statusMessage);
        }}
        onMovementOperationJobCreated={attachJob}
        onMovementOperationReset={resetOperation}
        onMoveTargetSelectionChange={handleMoveTargetSelectionChange}
        isPdfLoading={isPdfLoading}
        isMetadocFetching={isMetadocFetching}
        onSearch={isSearchEnabled ? handleSearch : undefined}
        availableTocDocumentTypes={tocDocumentTypes}
        selectedTocDocumentTypes={effectiveSelectedTocDocumentTypes}
        onTocDocumentTypesChange={handleTocDocumentTypesChange}
        tocSearchQuery={tocSearchQuery}
        onTocSearchQueryChange={handleTocFilterSearchChange}
        tocPageRangeStart={tocPageRange.startPage}
        tocPageRangeEnd={tocPageRange.endPage}
        onTocPageRangeChange={handleTocPageRangeChange}
        tocDateRangeStart={tocDateRange.startDate}
        tocDateRangeEnd={tocDateRange.endDate}
        onTocDateRangeChange={handleTocDateRangeChange}
        visiblePageRangeStart={visiblePageRangeStart}
        visiblePageRangeEnd={visiblePageRangeEnd}
        visiblePageNumbers={visiblePageNumbers}
        hideFilteredPages={hideFilteredPages}
        onHideFilteredPagesChange={setHideFilteredPages}
        summarySearchQuery={summarySearchQuery}
        onSummarySearchQueryChange={handleSummaryFilterSearchChange}
        onSummarySearchSubmit={() => {
          void handleSummarySearch(summarySearchQuery);
        }}
        onSummarySearchClear={() => {
          setSummarySearchQuery("");
          clearSummarySearch();
        }}
        isSummarySearching={isSummarySearching}
        summarySearchResultCount={summarySearchResultCount}
        lastSummarySearchQuery={lastSummarySearchQuery}
        summarySearchNavigableResultCount={summarySearchNavigableResultCount}
        onGoToPreviousSummarySearchResult={handlePreviousSummarySearchResult}
        onGoToNextSummarySearchResult={handleNextSummarySearchResult}
        medicationSearchQuery={medicationSearchQuery}
        onMedicationSearchQueryChange={handleMedicationFilterSearchChange}
        onMedicationSearchClear={() => {
          setMedicationSearchQuery("");
          setActiveMedicationSearchResultIndex(0);
        }}
        medicationSearchResultCount={matchingMedicationDocumentGroupIds.size}
        lastMedicationSearchQuery={medicationSearchQuery}
        medicationSearchNavigableResultCount={
          matchingMedicationDocumentGroupIds.size
        }
        onGoToPreviousMedicationSearchResult={
          handlePreviousMedicationSearchResult
        }
        onGoToNextMedicationSearchResult={handleNextMedicationSearchResult}
        codeSearchQuery={codeSearchQuery}
        onCodeSearchQueryChange={handleCodeFilterSearchChange}
        onCodeSearchClear={() => {
          setCodeSearchQuery("");
          setActiveCodeSearchResultIndex(0);
        }}
        codeSearchResultCount={matchingCodeDocumentGroupIds.size}
        lastCodeSearchQuery={codeSearchQuery}
        codeSearchNavigableResultCount={matchingCodeDocumentGroupIds.size}
        onGoToPreviousCodeSearchResult={handlePreviousCodeSearchResult}
        onGoToNextCodeSearchResult={handleNextCodeSearchResult}
        hasAiRun={Boolean(metadoc.pre_ai_run_download_url)}
        searchIndexStatus={isSearchEnabled ? searchIndexStatus : undefined}
        isSearchIndexFetching={isSearchEnabled ? isSearchIndexFetching : false}
        isSearchReady={isSearchEnabled ? isSearchReady : false}
        onStartIndexing={isSearchEnabled ? handleStartIndexing : undefined}
        isStartingIndexing={isSearchEnabled ? isStartingIndexing : false}
        isSearching={isSearchEnabled ? isSearching : false}
        searchResultCount={isSearchEnabled ? searchResultCount : 0}
        searchNavigableResultCount={
          isSearchEnabled ? searchNavigableResultCount : 0
        }
        activeSearchResultIndex={
          isSearchEnabled ? activeSearchResultIndex : null
        }
        onGoToPreviousSearchResult={
          isSearchEnabled ? handlePreviousSearchResult : undefined
        }
        onGoToNextSearchResult={
          isSearchEnabled ? handleNextSearchResult : undefined
        }
        canGoToPreviousSearchResult={
          isSearchEnabled ? canGoToPreviousSearchResult : false
        }
        canGoToNextSearchResult={
          isSearchEnabled ? canGoToNextSearchResult : false
        }
        lastSearchQuery={isSearchEnabled ? lastSearchQuery : ""}
      />
      <div className="metadoc-viewer-workspace__content">
        <div className="metadoc-viewer-workspace__toc">
          <MetaDocTableOfContents
            entries={tocEntries}
            currentPage={currentPage}
            onPageSelect={handlePageChange}
            documentGroups={documentGroups}
            onDeleteEntry={lazyTocProps?.onDeleteEntry ?? deleteEntry}
            metadocId={metadoc.id}
            metadocName={metadoc.name}
            disabled={isTocDisabled}
            selectedPages={selectedPages}
            onAssignDocumentType={assignDocumentType}
            onAssignDocumentTypeToRange={assignDocumentTypeToRange}
            onAssignDocumentGroupTag={assignDocumentGroupTag}
            onRemoveDocumentGroupTag={removeDocumentGroupTag}
            totalPages={totalPages}
            onDocumentTypeChangeComplete={() => {
              thumbnailsRef.current?.focusThumbnails();
            }}
            pageClassificationStats={pageClassificationStats}
            metadocStatus={metadoc.status}
            hasUntaggedPages={untaggedPages.length > 0}
            onGoToUntaggedPage={goToUntaggedPage}
            cloneReason={metadoc.clone_reason ?? null}
            groupingSource={effectiveGroupingSource}
            onGroupingSourceChange={onGroupingSourceChange}
            canShowGroupingToggle={canShowEffectiveGroupingToggle}
            isOpsPostAiReadOnly={isOpsPostAiReadOnly}
            selectedDocumentTypes={effectiveSelectedTocDocumentTypes}
            hasExplicitDocumentTypeSelection={selectedTocDocumentTypes !== null}
            searchQuery={tocSearchQuery}
            onSearchQueryChange={handleTocFilterSearchChange}
            pageRangeStart={tocPageRange.startPage}
            pageRangeEnd={tocPageRange.endPage}
            dateRangeStart={tocDateRange.startDate}
            dateRangeEnd={tocDateRange.endDate}
            matchingDateDocumentGroupIds={matchingDateDocumentGroupIds}
            matchingMedicationDocumentGroupIds={
              matchingMedicationDocumentGroupIds
            }
            matchingCodeDocumentGroupIds={matchingCodeDocumentGroupIds}
            medicationSearchQuery={medicationSearchQuery}
            codeSearchQuery={codeSearchQuery}
            onRegenerationJobCreated={(jobId) => {
              attachJob(jobId);
            }}
            onRegenerationRequestStateChange={(isRequesting) => {
              if (isRequesting) {
                startOperation("regenerate");
                return;
              }

              resetOperation();
            }}
            onPendingDocumentTypeChange={
              lazyTocProps?.onPendingDocumentTypeChange
            }
            onPendingDateChange={lazyTocProps?.onPendingDateChange}
            onPendingMetadataChange={lazyTocProps?.onPendingMetadataChange}
            onCancelPendingDelete={lazyTocProps?.onCancelPendingDelete}
            facilityByDocumentGroupIdOverride={
              lazyTocProps?.facilityByDocumentGroupIdOverride
            }
            hasPendingChanges={lazyTocProps?.hasPendingChanges ?? false}
            pendingChangeCount={lazyTocProps?.pendingChangeCount ?? 0}
            onApplyPendingChanges={lazyTocProps?.onApplyPendingChanges}
            onDiscardPendingChanges={lazyTocProps?.onDiscardPendingChanges}
            isApplyingPendingChanges={
              lazyTocProps?.isApplyingPendingChanges ?? false
            }
          />
        </div>
        <div className="metadoc-viewer-workspace__thumbnails">
          {pdfDoc && (
            <MetaDocPageThumbnails
              ref={thumbnailsRef}
              pdfDoc={pdfDoc}
              currentPage={currentPage}
              selectedPages={selectedPages}
              onPageSelect={handlePageChange}
              onSelectionChange={handleSelectionChange}
              onClearSelection={handleClearSelection}
              documentGroups={documentGroups}
              pageStates={pageStates}
              disableHotkeys={
                isProcessing ||
                isDeletingPages ||
                isMovingPages ||
                isOperationUiLocked
              }
              disabled={
                isProcessing ||
                isDeletingPages ||
                isMovingPages ||
                isOperationUiLocked
              }
              isLoading={
                isDeletingPages || isMovingPages || isOperationUiLocked
              }
              isSelectingMoveTarget={isSelectingMoveTarget}
              hideSelectionControls={
                isOpsPostAiReadOnly && !isSelectiveOpsReadonlyMode
              }
              lockClonedPagesOnly={isSelectiveOpsReadonlyMode}
              onMoveTargetSelect={onMoveTargetSelect || undefined}
              onCancelMoveTargetSelection={
                onCancelMoveTargetSelection || undefined
              }
              visiblePageNumbers={visiblePageNumbers}
            />
          )}
        </div>
        <div className="metadoc-viewer-workspace__pdf">
          {/* keep PDF viewer in DOM during processing to maintain layout stability */}
          <div
            className={`metadoc-viewer-workspace__pdf-container${isProcessing || isDeletingPages || isMovingPages || isOperationUiLocked ? " metadoc-viewer-workspace__pdf-container--hidden" : ""}`}
          >
            <MetaDocPdfViewer
              key={viewerDownloadIdentity}
              metadocId={metadoc.id}
              downloadUrl={viewerDownloadUrl}
              zoomPercent={zoomPercent}
              currentPage={currentPage}
              onCurrentPageChange={handleCurrentPageUpdate}
              onPdfDocLoad={setPdfDoc}
              onLoadingChange={handlePdfLoadingChange}
              pageStates={pageStates}
              disableScrollTracking={isDeletingPages || isMovingPages}
              searchHighlightsByPage={
                isSearchEnabled ? highlightsByPage : undefined
              }
              visiblePageNumbers={visiblePageNumbers}
            />
          </div>
          {/* overlay AI processing screen on top of PDF viewer */}
          {isProcessing && (
            <MetaDocAIProcessingScreen
              onComplete={onProcessingComplete!}
              jobStatus={processingJobStatus}
            />
          )}
          {/* overlay page deletion screen on top of PDF viewer */}
          {isDeletingPages && (
            <MetaDocOperationOverlayScreen
              classNameBase="metadoc-page-deletion"
              mainIcon="delete"
              title={`Deleting Page${deletingPageCount === 1 ? "" : "s"} ${formatPageRanges(deletingPageRanges)}`}
              statusText="In Progress"
            />
          )}
          {/* overlay page movement screen on top of PDF viewer */}
          {isMovingPages && (
            <MetaDocOperationOverlayScreen
              classNameBase="metadoc-page-movement"
              mainIcon="drive_file_move"
              title={`Moving Page${movingPageCount === 1 ? "" : "s"} ${formatPageRanges(movingPageRanges)} to Position ${movingTargetPosition}`}
              statusText="In Progress"
            />
          )}
          {isOperationUiLocked &&
            showOverlay &&
            operationState.operationType === "regenerate" &&
            overlayModel && (
              <MetaDocOperationOverlayScreen
                classNameBase="metadoc-page-movement"
                mainIcon="auto_fix_high"
                title={overlayModel.title}
                statusIcon={
                  overlayModel.statusText === "Saving Changes" ||
                  overlayModel.statusText === "Pending"
                    ? "schedule"
                    : "hourglass_empty"
                }
                statusText={overlayModel.statusText}
              />
            )}
        </div>
      </div>
    </div>
  );
};

export default MetaDocViewerWorkspace;
